// @ts-check

import {
  createAuthorizationUrl,
  createBusinessAccountOnboarding,
  getBusinessAccountOnboardingData
} from '@/api/businessAccount'

/**
 * @returns {SwanAccountModuleState}
 */
export const initSwanState = () => ({
  onboardingLink: null,
  onboardingData: null,
  onboardingIndustryOptions: [],
  onboardingPaymentVolumeOptions: []
})

export const swanAccountState = initSwanState()

/** @type {SwanAccountMutationTree} */
export const swanAccountMutations = {
  /**
   * Replaces the state with the given onboarding link
   *
   * @param {SwanAccountModuleState} state
   * @param {SwanAccountModuleState['onboardingLink']} link
   */
  setOnboardingLink: (state, link) => {
    state.onboardingLink = link
  },

  /**
   * Replaces the state with the given onboarding data
   *
   * @param {SwanAccountModuleState} state
   * @param {SwanAccountModuleState['onboardingData']} data
   */
  setOnboardingData: (state, data) => {
    state.onboardingData = data
  },

  /**
   * Replaces the state with the given onboarding towns
   *
   * @param {SwanAccountModuleState['onboardingIndustryOptions']} industries
   */
  setOnboardingIndustryOptions: (state, industries = []) => {
    const industryOptions = []

    industries.forEach((industry) => {
      industryOptions.push({
        label: `business_account.industry_option.${industry.translation_enum_key}`,
        value: industry.translation_enum_key,
        translate: true
      })
    })

    state.onboardingIndustryOptions = industryOptions
  },

  /**
   * Replaces the state with the given onboarding industry options
   *
   * @param {SwanAccountModuleState['onboardingPaymentVolumeOptions']} volumeOptions
   */
  setOnboardingPaymentVolumeOptions: (state, volumeOptions = []) => {
    const paymentVolumeOptions = []

    volumeOptions.forEach((option) => {
      paymentVolumeOptions.push({
        label: `business_account.payment_volume_option.${option.translation_enum_key}`,
        value: option.translation_enum_key,
        translate: true
      })
    })

    state.onboardingPaymentVolumeOptions = paymentVolumeOptions
  }
}

/** @type {SwanAccountGetterTree} */
export const swanAccountGetters = {}

/** @type {SwanAccountActionTree} */
export const swanAccountActions = {
  /**
   * Fetches the Swan onboarding link
   *
   * @param {SwanAccountActionContext} store
   */
  async submitSwanOnboarding({ state, commit }, payload) {
    const { onboardingLink } = state

    if (onboardingLink) {
      return onboardingLink
    }

    const { data: onboarding } = await createBusinessAccountOnboarding(payload)
    const { data: onboardingAuth } = await createAuthorizationUrl()

    commit('setOnboardingLink', onboardingAuth.redirect_url)

    return onboarding
  },

  /**
   * Fetches the Swan onboarding form data
   *
   * @param {SwanAccountActionContext} store
   */
  async getSwanOnboardingData({ state, commit }) {
    const { onboardingData } = state

    if (onboardingData) {
      return onboardingData
    }

    const onboardData = await getBusinessAccountOnboardingData()

    commit('setOnboardingData', onboardData)
    commit('setOnboardingIndustryOptions', onboardData?.business_enum)
    commit(
      'setOnboardingPaymentVolumeOptions',
      onboardData?.payment_volume_enum
    )
    return onboardData
  }
}

export const swanAccountModule = {
  namespaced: true,
  state: initSwanState,
  actions: swanAccountActions,
  mutations: swanAccountMutations,
  getters: swanAccountGetters
}

export default swanAccountModule
